import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { useToast } from "@/components/ui/use-toast";
import { Loader } from "@/components/ui/loader";
import { useGenerateAgtechReport, useGetAgtechSyncStats } from "@/lib/api/agtechReports";
import { downloadFromUrl } from "@/lib/browser/download";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Download, RefreshCcw, CheckCircle2, Clock, XCircle, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export function AgtechReportsPage() {
  const { toast } = useToast();
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>(() => {
    const yesterday = moment().tz(CommonDestinationTimezone).subtract(1, "day").toDate();
    return { from: yesterday, to: yesterday };
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const generateReport = useGenerateAgtechReport();
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useGetAgtechSyncStats();

  const isHalted = useMemo(() => {
    if (!statsData?.data) return false;
    return statsData.data.halted > 0;
  }, [statsData]);

  const formatDateForAPI = (date: Date): string => {
    return moment(date).tz(CommonDestinationTimezone).format("YYYY-MM-DD");
  };

  const handleGenerateReport = async () => {
    if (!dateRange.from || !dateRange.to) {
      toast({
        title: "Date Range Required",
        description: "Please select a date range",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setDownloadUrl(null);

    try {
      const result = await generateReport.mutateAsync({
        start_date: formatDateForAPI(dateRange.from),
        end_date: formatDateForAPI(dateRange.to),
      });

      if (!result.success || !result.data?.presigned_get_url) {
        toast({
          title: "Report Generation Failed",
          description: result.message || "Failed to generate report",
          variant: "destructive",
        });
        setDownloadUrl(null);
        return;
      }

      const presignedUrl = result.data.presigned_get_url;
      setDownloadUrl(presignedUrl);

      const filename = `agtech-report-${formatDateForAPI(dateRange.from)}-to-${formatDateForAPI(dateRange.to)}.xlsx`;
      await downloadFromUrl(presignedUrl, filename);

      toast({
        title: "Report Generated",
        description: "Your report has been downloaded",
      });
    } catch (error) {
      console.error("Failed to generate report", error);
      toast({
        title: "Report Generation Failed",
        description: String(error),
        variant: "destructive",
      });
      setDownloadUrl(null);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Agtech Dashboard</h1>
          <p className="text-muted-foreground">Monitor sync status and generate release reports.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetchStats()} disabled={statsLoading}>
          <RefreshCcw className={cn("h-4 w-4 mr-2", statsLoading && "animate-spin")} />
          Refresh Stats
        </Button>
      </div>

      <Separator />

      {/* Stats Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Sync Status
            {isHalted && <Badge variant="destructive">Halted</Badge>}
            {!isHalted && !statsLoading && <Badge variant="default" className="bg-green-500">Active</Badge>}
          </h2>
        </div>

        {isHalted && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              The sync process is currently halted. New data will not be processed until resumed.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Pending"
            value={statsData?.data?.pending ?? 0}
            icon={<Clock className="h-4 w-4 text-amber-500" />}
            loading={statsLoading}
            description="Queued for processing"
          />
          <StatCard
            title="Processing"
            value={statsData?.data?.processing ?? 0}
            icon={<RefreshCcw className="h-4 w-4 text-blue-500" />}
            loading={statsLoading}
            description="Items currently in sync"
          />
          <StatCard
            title="Failed"
            value={statsData?.data?.failed ?? 0}
            icon={<XCircle className="h-4 w-4 text-red-500" />}
            loading={statsLoading}
            description="Errors encountered today"
            variant={statsData?.data?.failed && statsData.data.failed > 0 ? "destructive" : "default"}
          />
        </div>
      </div>

      <Separator />

      {/* Reports Section */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="h-5 w-5 text-primary" />
                Release Report
              </CardTitle>
              <CardDescription>
                Generate an Excel report for agtech releases within a specific date range.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Date Range</label>
                <DateRangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  className="w-full"
                />
              </div>

              <div className="flex items-center gap-4 pt-2">
                <Button
                  onClick={handleGenerateReport}
                  disabled={isGenerating || !dateRange.from || !dateRange.to}
                  className="w-full md:w-auto"
                >
                  {isGenerating ? (
                    <>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Generate Excel
                    </>
                  )}
                </Button>

                {downloadUrl && !isGenerating && (
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download Last
                  </a>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Placeholder for future reports */}
          {/* <Card className="border-dashed bg-muted/20">
            <CardHeader>
              <CardTitle className="text-lg text-muted-foreground">More Reports Coming Soon</CardTitle>
              <CardDescription>
                Additional Agtech reporting capabilities are being developed.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center py-6">
              <div className="flex flex-col items-center text-muted-foreground opacity-50">
                <AlertTriangle className="h-12 w-12 mb-2" />
                <p className="text-sm italic">New reporting modules will appear here.</p>
              </div>
            </CardContent>
          </Card> */}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  description,
  loading = false,
  variant = "default"
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description?: string;
  loading?: boolean;
  variant?: "default" | "destructive";
}) {
  return (
    <Card className={cn(
      "overflow-hidden transition-all hover:shadow-md",
      variant === "destructive" && value !== 0 ? "border-red-200 bg-red-50/30" : ""
    )}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-8 flex items-center">
            <Loader className="h-4 w-4" />
          </div>
        ) : (
          <>
            <div className={cn(
              "text-2xl font-bold",
              variant === "destructive" && value !== 0 ? "text-red-600" : ""
            )}>
              {value}
            </div>
            {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}
